"use client";

import Image from "next/image";
import { Calendar, Clock, Tag } from "lucide-react";
import { Link } from "@/i18n/routing";
import { BlogPostSummary } from "@/lib/blog";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { formatReadingTime } from "@/lib/format-reading-time";

type BlogCardProps = {
  post: BlogPostSummary;
  ctaLabel: string;
};

const formatDate = (dateString: string) => {
  try {
    // Normalize date string to ensure consistent parsing
    const normalizedDate = dateString.trim();
    const date = new Date(normalizedDate);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  } catch {
    return dateString;
  }
};

export const BlogCard = ({ post, ctaLabel }: BlogCardProps) => {
  const { slug, title, excerpt, coverImage, date, readingTime, tags, category } =
    post;
  const t = useTranslations("blog");
  const formattedReadingTime = formatReadingTime(readingTime, t);

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white transition hover:border-[#8100FF]/40 hover:shadow-lg">
      <div className="relative w-full aspect-[16/9] bg-gray-100 overflow-hidden">
        {coverImage ? (
          <Image
            src={coverImage}
            alt={title}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            className="object-contain transition duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 text-gray-400">
            GRITIZER
          </div>
        )}
        {category && (
          <span className="absolute left-4 top-4 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-gray-700 shadow-sm">
            {category}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-4 p-6">
        <div className="flex flex-wrap items-center gap-4 text-xs uppercase tracking-widest text-gray-500">
          <span className="inline-flex items-center gap-2">
            <Calendar size={16} className="text-gray-400" />
            {formatDate(date)}
          </span>
          {formattedReadingTime && (
            <span className="inline-flex items-center gap-2">
              <Clock size={16} className="text-gray-400" />
              {formattedReadingTime}
            </span>
          )}
        </div>
        <Link
          href={`/blog/${slug}`}
          className="text-2xl font-semibold leading-tight text-gray-900 transition hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text"
        >
          {title}
        </Link>
        <p className="line-clamp-3 text-sm text-gray-600 font-poppins">{excerpt}</p>
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-2 text-xs text-gray-600">
            {tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-3 py-1"
              >
                <Tag size={14} className="text-[#8100FF]" />
                <span>{tag}</span>
              </span>
            ))}
            {tags.length > 3 && (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}
        <div className="mt-auto">
          <Link
            href={`/blog/${slug}`}
            className={cn(
              "inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition",
              "hover:border-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:text-white"
            )}
          >
            {ctaLabel}
          </Link>
        </div>
      </div>
    </article>
  );
};

